(() => {
  class AspadzProductComponentsCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.progressTimer = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleManualNavigation = this.handleManualNavigation.bind(this);
    }

    connectedCallback() {
      this.addMotionListener();
      this.addEventListener('click', this.handleManualNavigation);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      window.clearTimeout(this.progressTimer);
      this.removeMotionListener();
      this.removeEventListener('click', this.handleManualNavigation);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    addMotionListener() {
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.addListener(this.handleMotionPreferenceChange);
      }
    }

    removeMotionListener() {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionPreferenceChange);
      }
    }

    init() {
      if (this.swiper) return;

      const slider = this.querySelector('[data-aspadz-product-components-carousel-slider]');
      const slides = this.querySelectorAll('.aspadz-product-components-carousel__slide');

      if (!slider || slides.length < 2) {
        this.handleMotionPreferenceChange();
        return;
      }

      if (typeof window.AspadzConceptCarousel === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      const delay = Number(this.dataset.autoplayDelay) || 5000;
      const paginationLabel = this.dataset.paginationLabel || 'Slider';
      const pagination = this.querySelector('[data-aspadz-product-components-carousel-pagination]');
      const previousButton = this.querySelector('[data-aspadz-product-components-carousel-prev]');
      const nextButton = this.querySelector('[data-aspadz-product-components-carousel-next]');
      this.style.setProperty('--aspadz-pcc-delay', `${delay}ms`);

      this.swiper = new window.AspadzConceptCarousel(slider, {
        allowTouchMove: true,
        autoplay: this.motionQuery.matches
          ? false
          : {
              delay,
              disableOnInteraction: false,
            },
        loop: false,
        navigation:
          previousButton && nextButton
            ? {
                nextEl: nextButton,
                prevEl: previousButton,
              }
            : undefined,
        pagination: pagination
          ? {
              clickable: true,
              el: pagination,
              renderBullet(index, className) {
                return `
                  <button class="${className}" type="button" aria-label="${paginationLabel} ${index + 1}">
                    <span class="aspadz-product-components-carousel__pagination-track">
                      <span class="aspadz-product-components-carousel__pagination-progress"></span>
                    </span>
                  </button>
                `;
              },
            }
          : undefined,
        slidesPerView: 'auto',
        spaceBetween: 15,
        speed: 650,
        watchOverflow: true,
        breakpoints: {
          1200: {
            spaceBetween: 12,
          },
        },
        on: {
          init: () => this.restartProgress(),
          // Keep the class applied while Swiper moves the active state to the next bullet.
          // Swiper 5 can omit transition-end after pagination clicks, so manual controls
          // also schedule a post-transition reset below.
          slideChange: () => {
            if (!this.motionQuery.matches) this.classList.add('is-progressing');
          },
          slideChangeTransitionEnd: () => this.restartProgress(),
          touchEnd: () => this.restartProgress(),
          autoplayResume: () => this.restartProgress(),
        },
      });

      this.handleMotionPreferenceChange();
    }

    stopProgress() {
      this.classList.remove('is-progressing');
    }

    restartProgress() {
      this.stopProgress();

      window.requestAnimationFrame(() => {
        if (!this.motionQuery.matches) {
          this.classList.add('is-progressing');
        }
      });
    }

    handleMotionPreferenceChange() {
      const reduceMotion = this.motionQuery.matches;
      this.classList.toggle('aspadz-product-components-carousel--reduced-motion', reduceMotion);

      if (!this.swiper?.autoplay) {
        this.classList.toggle('is-progressing', !reduceMotion);
        return;
      }

      if (reduceMotion) {
        this.swiper.autoplay.stop();
        this.stopProgress();
      } else {
        this.swiper.autoplay.start();
        this.restartProgress();
      }
    }

    handleManualNavigation(event) {
      const control = event.target.closest?.(
        '[data-aspadz-product-components-carousel-prev], [data-aspadz-product-components-carousel-next], .swiper-pagination-bullet'
      );

      if (!control || !this.contains(control)) return;

      // Run after Swiper's 650ms transition so the reset targets the newly active bullet,
      // including pagination-click paths where Swiper 5 omits transition-end.
      window.clearTimeout(this.progressTimer);
      this.progressTimer = window.setTimeout(() => this.restartProgress(), 700);
    }

    handleBlockSelect(event) {
      const slide = event.target.closest?.('.aspadz-product-components-carousel__slide');
      if (!slide || !this.contains(slide) || !this.swiper) return;

      const index = Number(slide.dataset.itemIndex);
      if (Number.isNaN(index)) return;

      this.swiper.slideTo(index);
      this.swiper.autoplay?.stop();
      this.stopProgress();
    }
  }

  if (!window.customElements.get('aspadz-product-components-carousel')) {
    window.customElements.define('aspadz-product-components-carousel', AspadzProductComponentsCarousel);
  }
})();
